import React, { useEffect, useState } from 'react';

import { Input } from '@components';
import { ShadowContainer } from '@styles/components/wrapper';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Form, Row } from 'antd';
import { DraftsTable } from '@redux/slices/order';
import { historyColumns, filterTypes, filterTypesStatus } from '../constants';
import { Table } from '@components/table';
import { FormItem, Option, Select } from '@styles/components/form';
import { useHistory } from 'react-router-dom';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { translate } from '@components/i18n';
import { exportTransferOrderReportHistory, fetchTransferReports, transferHistoryOrderSelector } from '@redux/slices/transfer-order/history';
import { TransferOrder } from '@models/transfer-order';
import { getFilterOptions } from '@utils/filter';
import { ExportButton } from '@styles/components/button';
import { fetchMoneySymbols, moneyTypeSelector } from '@redux/slices/money-type';
import { cleanObject } from '@utils/objects';
import { useParams } from '@hooks/use-params';
import { InputRange } from '@components/input';
import moment from 'moment';

export const HistoryBody: React.FC = () => {
    const params = useParams()
    const history = useHistory();
    const dispatch = useDispatch();

    const moneyType = useSelector(moneyTypeSelector)
    const historyState = useSelector(transferHistoryOrderSelector);

    const [form] = Form.useForm();

    const [queryState, setQueryState] = useState<string>('');
    const [optionsComparisonFilter, setOptionsComparisonFilter] = useState(null);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFiltersChange = (changedValues: any, values: any): void => {
        if (values['datas'] && values['datas'].length > 0) {
            const range = values['datas'];
            values = {
                ...values,
                dataInicio: range[0].format('DD/MM/YYYY'),
                dataFim: range[1].format('DD/MM/YYYY'),
            };
        } else {
            values = {
                ...values,
                dataInicio: undefined,
                dataFim: undefined,
            };
        }

        delete values.datas

        if (changedValues.filterType) {
            setOptionsComparisonFilter(
                getFilterOptions(filterTypes, changedValues.filterType),
            );
        }

        dispatch(fetchTransferReports({
            ...values,
            filterType: form.getFieldValue('filterType'),
            filterValue: queryState,
            page: 0,
            size: 20,
        }));
    };

    const search = (): void => {
        dispatch(
            fetchTransferReports({
                filterType: form.getFieldValue('filterType'),
                comparisonType: form.getFieldValue("comparisonType"),
                filterValue: queryState,
                size: 20,
                page: 0,
            }),
        );
    };

    const onSearch = (value: string): void => {
        setQueryState(value);
    };

    const onSelect = (rowKey: DraftsTable, _, x): void => {
        if (x.target.dataset.event !== 'sales-order') {
            history.push(`/transfer-order/show?ref=${rowKey.id}`);
        }
    };

    const loadData = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pagination: any,
        filters: Record<string, FilterValue>,
        sorter: SorterResult<unknown>,
    ): void => {
        const params: TransferOrder.Params = {
            filterType: form.getFieldValue('filterType'),
            comparisonType: form.getFieldValue("comparisonType"),
            filterValue: queryState,
            page: pagination.current - 1,
            size: pagination.pageSize,
        }

        if (sorter.order && sorter.field) {
            Object.assign(params, {
                sort: `${sorter.field},${sorter.order.substring(0, 3)}`
            })
        }

        dispatch(fetchTransferReports(params));
    };

    const start = (): void => {
        const filters = {
            size: 20,
            page: 0,
            ...cleanObject(params),
        }

        const datas = []

        if (filters.dataInicio) {
            datas.push(moment(filters.dataInicio, 'DD/MM/YYYY'))
        }
        if (filters.dataFim) {
            datas.push(moment(filters.dataFim, 'DD/MM/YYYY'))
        }

        form.setFieldsValue({
            ...cleanObject(params),
            datas,
        })
        setQueryState(filters?.filterValue || '')

        dispatch(fetchTransferReports(cleanObject(filters)))

        if (!moneyType?.isSuccessSymbols) {
            dispatch(fetchMoneySymbols())
        }
    };

    const onExport = (): void => {
        dispatch(exportTransferOrderReportHistory({
            comparisonType: form.getFieldValue("comparisonType"),
            filterType: form.getFieldValue("filterType"),
            filterValue: queryState,
            page: 0,
            size: historyState.available.totalElements || 999,
        }));
    };

    useEffect(() => {
        setOptionsComparisonFilter(getFilterOptions(filterTypes, 'PEDIDO'));
        start();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(cleanObject(historyState.requestParams))
        history.replace(
            '/reports/transfer-orders?' + params.toString(),
        );
    }, [historyState.requestParams])

    return (
        <ShadowContainer>
            <Row justify="center">
                <Col span={23}>
                    <Table
                        columns={historyColumns}
                        rowKey={(record: DraftsTable) => `${record.id}`}
                        pointer
                        dataSource={historyState?.reports?.content || []}
                        pagination={{
                            current: (historyState?.reports?.number || 0) + 1,
                            pageSize: historyState?.reports?.size || 10,
                            total: historyState?.reports?.totalElements || 0
                        }}
                        isLoading={historyState?.isFetchingReports}
                        onRowClick={onSelect}
                        onFilterChange={onFiltersChange}
                        onChange={loadData}
                        form={form}
                        filters={[
                            <FormItem
                                label={translate('forms.labels.searchFor')}
                                name="filterType"
                                style={{ width: '200px' }}
                            >
                                <Select
                                    placeholder={translate('forms.placeholders.chooseFilter')}
                                    allowClear
                                >
                                    {filterTypes.map((f) => {
                                        return (
                                            <Option key={f.name} value={f.name}>
                                                {f.description}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormItem>,
                            <FormItem
                                label={translate('forms.labels.searchType')}
                                name="comparisonType"
                            >
                                <Select
                                    placeholder="Todos"
                                    dropdownMatchSelectWidth={false}
                                    allowClear
                                >
                                    {optionsComparisonFilter?.map(
                                        (f) => {
                                            return (
                                                <Option
                                                    key={f.name}
                                                    value={f.name}
                                                >
                                                    {f.description}
                                                </Option>
                                            );
                                        },
                                    )}
                                </Select>
                            </FormItem>,
                            <Form.Item wrapperCol={{ span: 24 }}>
                                <Input
                                    placeholder={translate(
                                        'forms.placeholders.search',
                                    )}
                                    allowClear
                                    suffix={
                                        <div
                                            onClick={search}
                                            style={{
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <SearchOutlined />
                                        </div>
                                    }
                                    onKeyUp={(e) => {
                                        if (
                                            e.code === `Enter` ||
                                            e.keyCode === 13
                                        ) {
                                            search();
                                        }
                                    }}
                                    onChange={(e) =>
                                        onSearch(e.target.value)
                                    }
                                />
                            </Form.Item>,
                            <FormItem
                                label={translate('forms.labels.date')}
                                name={'datas'}
                            >
                                <InputRange
                                    format="DD-MM-YYYY"
                                    placeholder={[
                                        translate(
                                            'forms.placeholders.startDate',
                                        ),
                                        translate('forms.placeholders.endDate'),
                                    ]}
                                    light
                                />
                            </FormItem>,
                            <FormItem
                                label={translate('forms.labels.status')}
                                name="status"
                                wrapperCol={{ style: { minWidth: 120 } }}
                            >
                                <Select
                                    placeholder={translate('general.all')}
                                    dropdownMatchSelectWidth={false}
                                    allowClear
                                >
                                    {filterTypesStatus.map((c) => (
                                        <Option value={c.name}>
                                            {c.description}
                                        </Option>
                                    ))}
                                </Select>
                            </FormItem>,
                        ]}
                        actions={[
                            <ExportButton
                                onClick={onExport}
                            >
                                {translate('general.export')}{' '}
                                <DownloadOutlined />
                            </ExportButton>,
                        ]}
                    />
                </Col>
            </Row>
        </ShadowContainer>
    );
};
